/**
 * useGenerationCreditInfo
 *
 * Computes cost, hasEnoughCredits, presetOptions from config + pricingData.
 * Max 50 lines.
 */
import { useQuery } from '@tanstack/react-query';
import { getCreditPricing } from '@/lib/api/credits';
import type { WorkspaceConfig, CustomPreset } from '@/types';

interface Options {
  config: WorkspaceConfig | null | undefined;
  resolution: string;
  imageCount: string;
}

export function useGenerationCreditInfo({ config, resolution, imageCount }: Options) {
  const { data: pricingData } = useQuery({ queryKey: ['credit-pricing'], queryFn: getCreditPricing, staleTime: 60 * 60 * 1000 });

  const imageCountNum = parseInt(imageCount) || 1;
  const featureCode = resolution === '4k' ? 'image_generation_4k' : 'image_generation';
  const creditsPerImage = (pricingData as { pricing?: Array<{ feature_code: string; effective_credits: number }> } | undefined)?.pricing?.find(p => p.feature_code === featureCode)?.effective_credits ?? 0;
  const cost = creditsPerImage * imageCountNum;
  const credits = config?.user_credits ?? 0;
  const hasEnoughCredits = credits >= cost;
  const canUseCustomPresets = config?.can_use_custom_presets ?? config?.can_use_custom_instructions ?? false;
  const allPresets: CustomPreset[] = config?.custom_presets || config?.custom_instructions || [];
  // Single-Shot ist Bildgenerierung: Text-Templates ausblenden (fehlendes template_type = "image", analog Backend)
  const customPresets: CustomPreset[] = allPresets.filter(p => (p.template_type ?? 'image') === 'image');

  const presetOptions = customPresets.map(p => ({ value: `custom:${p.id}`, label: p.name, imageUrl: p.preview_image ?? null }));

  return { creditsPerImage, cost, credits, hasEnoughCredits, canUseCustomPresets, presetOptions, customPresets };
}
